import type { JSX } from "preact";
import { useEffect, useState } from "preact/hooks";
import { BarChart, PieChart, Table } from "@gss/components/icons";
import { isDimensionValue } from "@gss/types/Dimension";
import type {
    MeasurementStat,
    MeasurementStatDimension,
} from "@gss/types/measurementStat";
import { Chart } from "../charts/Chart";
import type { ChartData } from "../charts/Chart";
import type {
    FilterSerie,
    FilterSeriesDimensionScore,
    FilterSeriesStat,
} from "../filters/filters.types";
import { getDimensionIconUrl } from "../resources";
import { round1 } from "../util/formatters";
import { NavButtons } from "../util/NavButtons";
import { StatsTable } from "../util/StatsTable";
import { TooFewRespondents } from "../util/TooFewRespondents";
import { useTranslation } from "../util/Translation";
import { getScoreSeries } from "./getScoreSeries";

interface Props {
    stat: MeasurementStat;
    dimensions: MeasurementStatDimension[];
    subtitle: string;
    filterSeries: FilterSerie[];
}

export function DimensionScores({
    stat,
    dimensions,
    subtitle,
    filterSeries,
}: Props): JSX.Element {
    const [series, setSeries] = useState<FilterSeriesDimensionScore[]>();
    const [seriesNotOk, setSeriesNotOk] = useState<FilterSeriesStat[]>();
    const [tab, setTab] = useState(0);
    const { translation, translateDimension } = useTranslation();
    const categories = dimensions.map((d) => d.name);
    const t = translation.stats;
    const dimScoreText = t.dimensionScores;
    const spiderChartText = t.spiderChart;
    const barChartText = t.barChart;
    const tableText = t.tableChart;

    useEffect(() => {
        void getScoreSeries(stat.securityContext, filterSeries).then(
            ({ ok, notOk }) => {
                setSeries(ok);
                setSeriesNotOk(notOk);
            },
        );
    }, [filterSeries]);

    const renderDimensionsButtonRow = () => (
        <NavButtons
            current={tab}
            onClick={(b) => setTab(b.id)}
            buttons={[
                { id: 0, icon: PieChart, title: spiderChartText },
                { id: 1, icon: BarChart, title: barChartText },
                { id: 2, icon: Table, title: tableText },
            ]}
            helpText={t.dimensionScoreDesc}
        />
    );

    const renderSpider = () => {
        return (
            <Chart
                variant="spider"
                title={dimScoreText}
                subtitle={subtitle}
                filename={`${stat.measurement} ${dimScoreText} ${spiderChartText}`}
                yTitle={null}
                categories={categories}
                series={series}
                callback={(s) => getDimensionsData(dimensions, s)}
                xFormatter={(e) => {
                    if (
                        typeof e.value !== "string" ||
                        !isDimensionValue(e.value)
                    ) {
                        return "";
                    }
                    const name = translateDimension(e.value);
                    const icon = getDimensionIcon(dimensions, e.value);
                    return icon != null ? `${icon}${name}` : name;
                }}
            />
        );
    };

    const renderBar = () => {
        return (
            <Chart
                variant="bar"
                title={dimScoreText}
                subtitle={subtitle}
                filename={`${stat.measurement} ${dimScoreText} ${barChartText}`}
                yTitle={t.dimensionScoreYAxis}
                categories={categories}
                series={series}
                callback={(s) => getDimensionsData(dimensions, s)}
                xFormatter={(e) => {
                    if (
                        typeof e.value !== "string" ||
                        !isDimensionValue(e.value)
                    ) {
                        return "";
                    }
                    const name = translateDimension(e.value);
                    const icon = getDimensionIcon(dimensions, e.value);
                    return icon != null ? `${name}${icon}` : name;
                }}
            />
        );
    };

    const renderTable = () => (
        <StatsTable
            className="chart-spider"
            title={dimScoreText}
            subtitle={subtitle}
            categories={categories.map(translateDimension)}
            series={series}
            callback={(s) =>
                getDimensionsData(dimensions, s).data.map((d) =>
                    d != null ? round1(d) : null,
                )
            }
        />
    );

    const renderTotalScore = () => {
        const title = dimensions.some((d) => d.name === "Helhetsintryck")
            ? `${t.dimensionScoreTotal} (${t.dimensionScoreTotalParentheses})`
            : t.dimensionScoreTotal;
        return (
            <Chart
                variant="column"
                title={title}
                subtitle={subtitle}
                filename={`${stat.measurement} ${title}`}
                yTitle={t.dimensionScoreTotalYAxis}
                categories={undefined}
                series={series}
                callback={getTotalData}
            />
        );
    };

    return (
        <>
            <TooFewRespondents series={seriesNotOk} />
            {renderDimensionsButtonRow()}
            {tab === 0 && renderSpider()}
            {tab === 1 && renderBar()}
            {tab === 2 && renderTable()}
            <hr />
            <NavButtons helpText={t.dimensionScoreTotalDesc} />
            {renderTotalScore()}
        </>
    );
}

function getDimensionsData(
    dimensions: MeasurementStatDimension[],
    serie: FilterSeriesDimensionScore,
): Omit<ChartData, "data"> & { data: (number | null)[] } {
    if (serie.stat.status === "nok") {
        return { data: dimensions.map(() => null) };
    }
    const data: (number | null)[] = [];
    const confidenceIntervals: ChartData["confidenceIntervals"] = {};
    for (const dim of dimensions) {
        const statDim = serie.stat.dimensions.find((d) => d.name === dim.name);
        if (statDim != null) {
            data.push(statDim.score);
            confidenceIntervals[dim.name] = statDim.confidenceInterval;
        } else {
            data.push(null);
        }
    }
    return { data, confidenceIntervals };
}

function getTotalData(serie: FilterSeriesDimensionScore): ChartData {
    if (serie.stat.status === "nok") {
        return { data: [null] };
    }
    return {
        data: [serie.stat.totalScore?.score ?? null],
        confidenceInterval: serie.stat.totalScore?.confidenceInterval,
    };
}

function getDimensionIcon(
    dimensions: MeasurementStatDimension[],
    dimension: string,
): string | undefined {
    const iconName = dimensions.find((d) => d.name === dimension)?.icon;
    if (iconName == null) {
        return undefined;
    }
    const url = getDimensionIconUrl(iconName);
    return `<img class="dimension-icon" aria-hidden="true" src="${url}"/>`;
}
